import { z } from "zod";

// Runtime config from /config.js (loaded via <script> before the app).
// DevOps replaces this file per environment — no rebuild needed.
interface RuntimeConfig {
    API_URL?: string;
    BILLING_API_URL?: string;
    YANDEX_METRICA_ID?: string;
    SENTRY_DSN?: string;
    SENTRY_ENV?: string;
    S3_PUBLIC_URL?: string;
}

declare global {
    interface Window {
        __SNTZ_CONFIG?: RuntimeConfig;
    }
}

const rc = typeof window !== "undefined" ? window.__SNTZ_CONFIG : undefined;

// Runtime config takes priority. VITE_* build-time vars are fallback.
// This lets DevOps deploy one build to multiple environments by
// swapping /config.js — no need for separate builds.
function pick(runtimeKey: keyof RuntimeConfig, viteKey: string): string | undefined {
    const runtime = rc?.[runtimeKey];
    if (runtime) return runtime;
    const buildTime = import.meta.env[viteKey] as string | undefined;
    return buildTime ?? undefined;
}

const envSchema = z.object({
    VITE_API_URL: z.string().url(),
    VITE_BILLING_API_URL: z.string().url(),
    VITE_SENTRY_DSN: z.string().optional(),
    VITE_SENTRY_ENV: z.string().default("dbg"),
    VITE_YANDEX_METRICA_ID: z.string().optional(),
    VITE_S3_PUBLIC_URL: z.string().url().default("https://syntez-content.s3.eu-central-1.amazonaws.com/"),
});

export const env = envSchema.parse({
    VITE_API_URL: pick("API_URL", "VITE_API_URL"),
    VITE_BILLING_API_URL: pick("BILLING_API_URL", "VITE_BILLING_API_URL"),
    VITE_SENTRY_DSN: pick("SENTRY_DSN", "VITE_SENTRY_DSN"),
    VITE_SENTRY_ENV: pick("SENTRY_ENV", "VITE_SENTRY_ENV"),
    VITE_YANDEX_METRICA_ID: pick("YANDEX_METRICA_ID", "VITE_YANDEX_METRICA_ID"),
    VITE_S3_PUBLIC_URL: pick("S3_PUBLIC_URL", "VITE_S3_PUBLIC_URL"),
});
